import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pill, Check, X, Clock, AlertCircle, Printer, Edit2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import DictableTextarea from '@/components/DictableTextarea';
import { openPrintWindow } from '@/lib/letterhead';
import DatePickerField from '@/components/DatePickerField';

const statusIcons = {
  pending: { icon: Clock, label: 'In attesa', class: 'bg-slate-100 text-slate-600' },
  somministrato: { icon: Check, label: 'Somministrato', class: 'bg-emerald-100 text-emerald-700' },
  rifiutato: { icon: X, label: 'Rifiutato', class: 'bg-red-100 text-red-700' },
  non_somministrato: { icon: X, label: 'Non somm.', class: 'bg-amber-100 text-amber-700' },
  non_disponibile: { icon: AlertCircle, label: 'Non disp.', class: 'bg-slate-100 text-slate-500' },
};

export default function Terapie() {
  const { hasRole } = useAuth();
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [terapie, setTerapie] = useState([]);
  const [somm, setSomm] = useState([]);
  const [ospiti, setOspiti] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtroOrari, setFiltroOrari] = useState([]);
  const [noteDialog, setNoteDialog] = useState(null);
  const [notaMotivo, setNotaMotivo] = useState('');
  const [abNoteDialog, setAbNoteDialog] = useState(null);
  const [abNota, setAbNota] = useState('');
  const ORARI_PRESET = ['06:00','07:00','08:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','18:00','19:00','20:00','21:00','22:00'];
  const toggleFiltroOrario = (o) => setFiltroOrari(prev => prev.includes(o) ? prev.filter(x => x !== o) : [...prev, o]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [tRes, sRes, oRes] = await Promise.all([
          api.get('/terapie', { params: { stato: 'attiva' } }),
          api.get('/somministrazioni', { params: { data } }),
          api.get('/ospiti', { params: { stato: 'presente' } })
        ]);
        setTerapie(tRes.data);
        setSomm(sRes.data);
        const oMap = {};
        oRes.data.forEach(o => { oMap[o.id] = o; });
        setOspiti(oMap);
      } catch (e) { toast.error('Errore caricamento'); }
      setLoading(false);
    };
    fetchAll();
  }, [data]);

  const getSommStatus = (terapiaId, orario) => {
    const found = somm.find(s => s.terapia_id === terapiaId && s.orario === orario);
    return found?.stato || 'pending';
  };

  const getAbSomm = (terapiaId) => {
    return somm.filter(s => s.terapia_id === terapiaId && s.al_bisogno);
  };

  const handleSomministrazione = async (terapia, orario, stato, note = '') => {
    try {
      await api.post('/somministrazioni', {
        terapia_id: terapia.id,
        ospite_id: terapia.ospite_id,
        data,
        orario,
        stato,
        note,
      });
      toast.success(`${stato.replace(/_/g, ' ')} registrato`);
      const sRes = await api.get('/somministrazioni', { params: { data } });
      setSomm(sRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleAbSomministra = async (terapia, note = '') => {
    const now = new Date();
    const orario = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    try {
      await api.post('/somministrazioni', {
        terapia_id: terapia.id,
        ospite_id: terapia.ospite_id,
        data,
        orario,
        stato: 'somministrato',
        note,
      });
      toast.success(`Somministrazione AB registrata alle ${orario}`);
      const sRes = await api.get('/somministrazioni', { params: { data } });
      setSomm(sRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleNonSommConfirm = () => {
    if (!noteDialog) return;
    handleSomministrazione(noteDialog.terapia, noteDialog.orario, noteDialog.stato, notaMotivo);
    setNoteDialog(null);
    setNotaMotivo('');
  };

  const openNonSommDialog = (terapia, orario, stato) => {
    setNoteDialog({ terapia, orario, stato });
    setNotaMotivo('');
  };

  const handleAbNoteConfirm = () => {
    if (!abNoteDialog) return;
    handleAbSomministra(abNoteDialog.terapia, abNota);
    setAbNoteDialog(null);
    setAbNota('');
  };

  // Split therapies: scheduled vs AB
  const terapieSchedulate = terapie.filter(t => !t.al_bisogno);
  const terapieAB = terapie.filter(t => t.al_bisogno);

  // Group scheduled by ospite, filtering by selected times
  const groupedByOspite = {};
  terapieSchedulate.forEach(t => {
    const hasMatchingOrario = filtroOrari.length === 0 || t.orari.some(o => filtroOrari.includes(o));
    if (!hasMatchingOrario) return;
    if (!groupedByOspite[t.ospite_id]) groupedByOspite[t.ospite_id] = [];
    groupedByOspite[t.ospite_id].push(t);
  });

  // Group AB by ospite
  const abByOspite = {};
  terapieAB.forEach(t => {
    if (!abByOspite[t.ospite_id]) abByOspite[t.ospite_id] = [];
    abByOspite[t.ospite_id].push(t);
  });

  const handlePrintTurno = () => {
    const orariLabel = filtroOrari.length > 0 ? filtroOrari.join(', ') : 'Tutti';
    const rows = Object.entries(groupedByOspite).map(([oid, terList]) => {
      const osp = ospiti[oid];
      if (!osp) return '';
      const farmaci = terList.flatMap(t =>
        t.orari.filter(o => filtroOrari.length === 0 || filtroOrari.includes(o)).map(o => {
          const st = getSommStatus(t.id, o);
          return `<tr><td>${t.farmaco}</td><td>${t.dosaggio}</td><td>${t.via}</td><td style="font-family:monospace">${o}</td><td>${st === 'pending' ? '___________' : st}</td></tr>`;
        })
      ).join('');
      return `<div style="margin-bottom:14px;page-break-inside:avoid"><p class="section-title" style="margin:8px 0 4px">${osp.cognome} ${osp.nome} <span style="font-weight:normal;color:#64748b;font-size:10px">- Camera ${osp.stanza || '-'}/${osp.letto || '-'} ${osp.area ? '(' + osp.area + ')' : ''}</span></p><table><thead><tr><th>Farmaco</th><th>Dosaggio</th><th>Via</th><th>Orario</th><th>Firma</th></tr></thead><tbody>${farmaci}</tbody></table></div>`;
    }).join('');
    // AB section in print
    const abRows = Object.entries(abByOspite).map(([oid, terList]) => {
      const osp = ospiti[oid];
      if (!osp) return '';
      const farmaci = terList.map(t => {
        const abS = getAbSomm(t.id);
        const sommStr = abS.length > 0 ? abS.map(s => `${s.orario} (${s.stato})`).join(', ') : 'Non somministrato';
        return `<tr><td>${t.farmaco}</td><td>${t.dosaggio}</td><td>${t.via}</td><td>AL BISOGNO</td><td>${sommStr}</td></tr>`;
      }).join('');
      return `<div style="margin-bottom:14px;page-break-inside:avoid"><p class="section-title" style="margin:8px 0 4px">${osp.cognome} ${osp.nome} <span style="font-weight:normal;color:#64748b;font-size:10px">- Camera ${osp.stanza || '-'}/${osp.letto || '-'}</span></p><table><thead><tr><th>Farmaco</th><th>Dosaggio</th><th>Via</th><th>Tipo</th><th>Somministrazioni</th></tr></thead><tbody>${farmaci}</tbody></table></div>`;
    }).join('');
    const allRows = rows + (abRows ? `<div style="margin-top:20px;border-top:2px solid #f59e0b;padding-top:12px"><p style="font-weight:bold;color:#d97706;font-size:13px;margin-bottom:8px">TERAPIE AL BISOGNO</p>${abRows}</div>` : '');
    openPrintWindow('Scheda Somministrazione Terapia', allRows, {
      subtitle: `Orari: ${orariLabel} | Ospiti: ${Object.keys(groupedByOspite).length + Object.keys(abByOspite).length}`,
      date: new Date(data).toLocaleDateString('it-IT', {weekday:'long',day:'numeric',month:'long',year:'numeric'})
    });
  };

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="terapie-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] tracking-tight">Somministrazioni</h1>
          <p className="text-sm text-slate-500 mt-0.5">Piano terapeutico giornaliero (MAR)</p>
        </div>
        <div className="flex items-center gap-2">
          <DatePickerField testId="mar-date" value={data} onChange={v => setData(v)} className="w-44" />
          <Button size="sm" variant="outline" onClick={handlePrintTurno} data-testid="print-turno-btn">
            <Printer className="w-4 h-4 mr-1" />Stampa Turno
          </Button>
        </div>
      </div>
      {/* Filtro orari */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-500 font-medium flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Filtra orari:</span>
        {ORARI_PRESET.map(o => (
          <button key={o} onClick={() => toggleFiltroOrario(o)}
            className={`px-2.5 py-1 text-xs rounded-md border font-mono transition-all ${filtroOrari.includes(o) ? 'bg-brand text-white border-brand' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}
            data-testid={`filtro-orario-${o}`}>{o}</button>
        ))}
        {filtroOrari.length > 0 && <button onClick={() => setFiltroOrari([])} className="text-[10px] text-red-500 hover:text-red-700 underline">Rimuovi filtri</button>}
      </div>

      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="h-32 p-4" /></Card>)}</div>
      ) : (
        <>
          {/* === SCHEDULED THERAPIES === */}
          {Object.keys(groupedByOspite).length === 0 && terapieAB.length === 0 ? (
            <div className="text-center py-16"><Pill className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">{filtroOrari.length > 0 ? 'Nessuna terapia per gli orari selezionati' : 'Nessuna terapia attiva'}</p></div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedByOspite).map(([ospiteId, terList]) => {
                const osp = ospiti[ospiteId];
                if (!osp) return null;
                const filteredRows = terList.flatMap(t =>
                  t.orari.filter(orario => filtroOrari.length === 0 || filtroOrari.includes(orario)).map(orario => ({ t, orario }))
                );
                if (filteredRows.length === 0) return null;
                return (
                  <Card key={ospiteId} data-testid={`mar-patient-${ospiteId}`}>
                    <CardHeader className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-semibold text-blue-700">{osp.nome?.[0]}{osp.cognome?.[0]}</div>
                        <div>
                          <CardTitle className="text-sm font-semibold">{osp.cognome} {osp.nome}</CardTitle>
                          <p className="text-xs text-slate-400">Camera {osp.stanza}-{osp.letto} {osp.area && <span className="inline-flex items-center border rounded px-1 text-[9px] ml-1">{osp.area}</span>}</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="px-4 pb-4">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Farmaco</TableHead>
                            <TableHead className="text-xs">Dosaggio</TableHead>
                            <TableHead className="text-xs">Via</TableHead>
                            <TableHead className="text-xs">Orario</TableHead>
                            <TableHead className="text-xs">Stato</TableHead>
                            {hasRole('infermiere', 'coordinatore', 'medico') && <TableHead className="text-xs">Azione</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredRows.map(({ t, orario }) => {
                            const status = getSommStatus(t.id, orario);
                            const si = statusIcons[status] || statusIcons.pending;
                            const Icon = si.icon;
                            return (
                              <TableRow key={`${t.id}-${orario}`}>
                                <TableCell className="text-sm font-medium">{t.farmaco}</TableCell>
                                <TableCell className="text-sm">{t.dosaggio}</TableCell>
                                <TableCell className="text-sm capitalize">{t.via}</TableCell>
                                <TableCell className="text-sm font-mono">{orario}</TableCell>
                                <TableCell>
                                  <Badge className={`text-[10px] ${si.class}`}>
                                    <Icon className="w-3 h-3 mr-1" />{si.label}
                                  </Badge>
                                </TableCell>
                                {hasRole('infermiere', 'coordinatore', 'medico') && (
                                  <TableCell>
                                    {status === 'pending' ? (
                                      <div className="flex gap-1">
                                        <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-600 hover:text-emerald-700" onClick={() => handleSomministrazione(t, orario, 'somministrato')} data-testid={`somm-ok-${t.id}-${orario}`}>
                                          <Check className="w-3.5 h-3.5 mr-1" />OK
                                        </Button>
                                        <Select onValueChange={v => openNonSommDialog(t, orario, v)}>
                                          <SelectTrigger className="h-7 w-24 text-xs"><SelectValue placeholder="Altro" /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="rifiutato">Rifiutato</SelectItem>
                                            <SelectItem value="non_somministrato">Non somm.</SelectItem>
                                            <SelectItem value="non_disponibile">Non disp.</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    ) : (
                                      <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400 hover:text-brand" onClick={() => handleSomministrazione(t, orario, 'pending')} title="Correggi" data-testid={`somm-correggi-${t.id}-${orario}`}>
                                        <Edit2 className="w-3 h-3 mr-1" />Correggi
                                      </Button>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* === AB (AL BISOGNO) THERAPIES === */}
          {terapieAB.length > 0 && (
            <div className="space-y-4" data-testid="ab-section">
              <div className="flex items-center gap-2 pt-2 border-t border-amber-200">
                <Zap className="w-4 h-4 text-amber-600" />
                <h2 className="text-sm font-bold text-amber-800">Terapie Al Bisogno (AB)</h2>
                <Badge className="bg-amber-100 text-amber-700 text-[10px]">{terapieAB.length} terapie</Badge>
              </div>
              {Object.entries(abByOspite).map(([ospiteId, terList]) => {
                const osp = ospiti[ospiteId];
                if (!osp) return null;
                return (
                  <Card key={`ab-${ospiteId}`} className="border-amber-200/60" data-testid={`mar-ab-patient-${ospiteId}`}>
                    <CardHeader className="py-3 px-4 bg-amber-50/40">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-xs font-semibold text-amber-700">{osp.nome?.[0]}{osp.cognome?.[0]}</div>
                        <div>
                          <CardTitle className="text-sm font-semibold">{osp.cognome} {osp.nome}</CardTitle>
                          <p className="text-xs text-slate-400">Camera {osp.stanza}-{osp.letto} {osp.area && <span className="inline-flex items-center border rounded px-1 text-[9px] ml-1">{osp.area}</span>}</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="px-4 pb-4">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Farmaco</TableHead>
                            <TableHead className="text-xs">Dosaggio</TableHead>
                            <TableHead className="text-xs">Via</TableHead>
                            <TableHead className="text-xs">Somministrazioni Oggi</TableHead>
                            {hasRole('infermiere', 'coordinatore', 'medico') && <TableHead className="text-xs">Azione</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {terList.map(t => {
                            const abSommList = getAbSomm(t.id);
                            return (
                              <TableRow key={t.id}>
                                <TableCell className="text-sm font-medium">
                                  {t.farmaco}
                                  {t.note && <span className="block text-[10px] text-slate-400 italic">{t.note}</span>}
                                </TableCell>
                                <TableCell className="text-sm">{t.dosaggio}</TableCell>
                                <TableCell className="text-sm capitalize">{t.via}</TableCell>
                                <TableCell>
                                  {abSommList.length === 0 ? (
                                    <span className="text-xs text-slate-400">Nessuna</span>
                                  ) : (
                                    <div className="flex flex-wrap gap-1">
                                      {abSommList.map((s, i) => (
                                        <Badge key={i} className={`text-[10px] ${s.stato === 'somministrato' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                          {s.orario} - {s.operatore_nome?.split(' ')[0] || ''}
                                          {s.note && <span className="ml-1 opacity-70">({s.note})</span>}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </TableCell>
                                {hasRole('infermiere', 'coordinatore', 'medico') && (
                                  <TableCell>
                                    <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                                      onClick={() => { setAbNoteDialog({ terapia: t }); setAbNota(''); }}
                                      data-testid={`somm-ab-${t.id}`}>
                                      <Zap className="w-3.5 h-3.5 mr-1" />Somministra Ora
                                    </Button>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Non-somministrazione note dialog */}
      <Dialog open={!!noteDialog} onOpenChange={(v) => { if (!v) { setNoteDialog(null); setNotaMotivo(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Motivo Non Somministrazione</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Stato: <Badge className="text-[10px] bg-amber-100 text-amber-700">{noteDialog?.stato?.replace(/_/g, ' ')}</Badge></p>
            <div><Label className="text-xs">Motivo / Note *</Label><DictableTextarea value={notaMotivo} onChange={e => setNotaMotivo(e.target.value)} rows={3} placeholder="es. Paziente rifiuta, nausea, farmaco non disponibile..." data-testid="nota-non-somm" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => { setNoteDialog(null); setNotaMotivo(''); }}>Annulla</Button><Button onClick={handleNonSommConfirm} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="conferma-non-somm">Conferma</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AB somministrazione note dialog */}
      <Dialog open={!!abNoteDialog} onOpenChange={(v) => { if (!v) { setAbNoteDialog(null); setAbNota(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Somministrazione Al Bisogno</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-2 rounded bg-amber-50 border border-amber-200">
              <p className="text-xs font-semibold text-amber-800">{abNoteDialog?.terapia?.farmaco}</p>
              <p className="text-[10px] text-amber-600">{abNoteDialog?.terapia?.dosaggio} - {abNoteDialog?.terapia?.via}</p>
            </div>
            <p className="text-xs text-slate-500">Orario: <span className="font-mono font-bold">{new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span></p>
            <div><Label className="text-xs">Motivo / Note</Label><DictableTextarea value={abNota} onChange={e => setAbNota(e.target.value)} rows={2} placeholder="es. Cefalea, dolore, agitazione..." data-testid="nota-ab-somm" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setAbNoteDialog(null); setAbNota(''); }}>Annulla</Button>
              <Button onClick={handleAbNoteConfirm} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="conferma-ab-somm">
                <Check className="w-3.5 h-3.5 mr-1" />Conferma Somministrazione
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
